import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  FileText,
  ImagePlus,
  Loader2,
  PencilLine,
  Sparkles,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Card } from "@/platform/ui/card";
import { GradingProgress } from "@/platform/ui/grading-progress";
import { showCorrectionError } from "@/platform/billing/errors";
import { cn } from "@/platform/lib/cn";
import { useSession } from "@/platform/auth/use-session";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import {
  gradeEssayFuvest,
  listUspEssayPrompts,
  uploadUspEssayImages,
  type GradeEssayFuvestResult,
  type UspEssayPromptRow,
} from "@/tenants/usp/features/essay/api";

const ISSUE_LABELS: Record<string, string> = {
  copia_coletanea: "Cópia da coletânea",
  parafrase_excessiva: "Paráfrase excessiva",
  modelo_pronto: "Modelo pronto detectado",
  repertorio_inventado: "Repertório inventado/incorreto",
  fuga_tema: "Fuga ao tema",
  "gênero_incorreto": "Gênero incorreto",
};

const CRITERIA = [
  { key: "tema", label: "Tema", max: 25 },
  { key: "genero_modalidade", label: "Gênero/modalidade", max: 25 },
  { key: "coesao_coerencia", label: "Coesão e coerência", max: 25 },
  { key: "norma_culta", label: "Norma culta", max: 25 },
] as const;

export function UspRedacaoPage() {
  const { session } = useSession();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"text" | "image">("text");
  const [selectedPrompt, setSelectedPrompt] = useState<UspEssayPromptRow | null>(null);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<GradeEssayFuvestResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const promptsQuery = useQuery({
    queryKey: ["usp", "essay-prompts"],
    queryFn: listUspEssayPrompts,
  });

  const prompts = promptsQuery.data ?? [];
  const activePrompt = selectedPrompt ?? prompts[0] ?? null;

  async function handleSubmit() {
    if (!session) {
      toast.error("Faça login novamente.");
      return;
    }
    if (!activePrompt) {
      toast.error("Selecione um tema.");
      return;
    }
    if (mode === "text" && body.trim().length < 50) {
      toast.error("Escreva ao menos 50 caracteres.");
      return;
    }
    if (mode === "image" && files.length === 0) {
      toast.error("Anexe ao menos uma foto.");
      return;
    }

    setSubmitting(true);
    setResult(null);
    try {
      let imagePaths: string[] | undefined;
      if (mode === "image") {
        const uuid = crypto.randomUUID();
        imagePaths = await uploadUspEssayImages({
          userId: session.user.id,
          submissionUuid: uuid,
          files,
        });
      }

      const res = await gradeEssayFuvest({
        prompt_id: activePrompt.id,
        theme: activePrompt.theme,
        collection: activePrompt.collection ?? undefined,
        body: mode === "text" ? body : undefined,
        image_paths: imagePaths,
      });
      setResult(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      showCorrectionError(toast, err, navigate);
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return <RedacaoResult result={result} onReset={() => setResult(null)} />;
  }

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <GradingProgress
        active={submitting}
        steps={[
          "Lendo sua redação…",
          "Avaliando tema e gênero dissertativo-argumentativo…",
          "Conferindo coesão, coerência e norma culta…",
          "Checando repertório e uso da coletânea…",
          "Montando notas, feedback e plano de reescrita…",
        ]}
      />
      <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-20 blur-2xl animate-blob" />
      <SoftStar color="mint" className="pointer-events-none absolute right-12 top-32 w-12 opacity-50 animate-float" />

      <Container size="default" className="relative py-12 md:py-16">
        <Link
          to="/usp/painel"
          className="inline-flex items-center gap-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] transition-colors hover:text-[color:var(--color-foreground)]"
        >
          <ArrowLeft className="size-3.5" />
          Painel
        </Link>

        <header className="mt-8 max-w-2xl">
          <Badge variant="accentSolid" className="gap-2">
            <PencilLine className="size-3" />
            Redação Fuvest
          </Badge>
          <h1 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
            Treine a redação dissertativo-argumentativa.
          </h1>
          <p className="mt-3 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] leading-relaxed">
            Correção com a rubrica oficial Fuvest (4 critérios × 25 pontos). Texto digitado ou
            foto da redação manuscrita. Detector de cópia da coletânea + plano de reescrita.
          </p>
        </header>

        <Card className="mt-8 p-6 md:p-8">
          {/* Tema picker */}
          <div>
            <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
              Tema (proposta Fuvest)
            </label>
            {promptsQuery.isLoading ? (
              <Loader2 className="mt-2 size-4 animate-spin" />
            ) : (
              <select
                value={activePrompt?.id ?? ""}
                onChange={(e) => setSelectedPrompt(prompts.find((p) => p.id === e.target.value) ?? null)}
                className="mt-2 w-full rounded-lg border border-[--color-border] bg-[color:var(--color-background)] px-3.5 py-2.5 text-[length:var(--text-body-sm)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]"
              >
                {prompts.map((p) => (
                  <option key={p.id} value={p.id}>
                    Fuvest {p.year} — {p.theme.slice(0, 80)}
                  </option>
                ))}
              </select>
            )}
          </div>

          {activePrompt && (
            <div className="mt-5 rounded-xl border border-[--color-border] bg-[color:var(--color-muted)]/30 p-4">
              <p className="text-[length:var(--text-body-sm)] font-medium">{activePrompt.theme}</p>
              {activePrompt.collection && activePrompt.collection.length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                    Coletânea ({activePrompt.collection.length} textos)
                  </summary>
                  <ul className="mt-3 space-y-3">
                    {activePrompt.collection.map((c) => (
                      <li key={c.id} className="text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                        <span className="font-medium">{c.source}:</span> {c.excerpt}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* Mode toggle */}
          <div className="mt-6 inline-flex rounded-full border border-[--color-border] bg-[color:var(--color-background)] p-1">
            <button
              type="button"
              onClick={() => setMode("text")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[length:var(--text-body-sm)] font-medium transition-colors",
                mode === "text" ? "bg-[color:var(--color-accent)] text-white" : "text-[color:var(--color-muted-foreground)]",
              )}
            >
              <FileText className="size-3.5" />
              Digitar
            </button>
            <button
              type="button"
              onClick={() => setMode("image")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[length:var(--text-body-sm)] font-medium transition-colors",
                mode === "image" ? "bg-[color:var(--color-accent)] text-white" : "text-[color:var(--color-muted-foreground)]",
              )}
            >
              <ImagePlus className="size-3.5" />
              Foto manuscrita
            </button>
          </div>

          {mode === "text" ? (
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              placeholder="Cole ou escreva sua redação aqui..."
              className="mt-4 w-full rounded-xl border border-[--color-border] bg-[color:var(--color-background)] p-4 text-[length:var(--text-body-sm)] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]"
            />
          ) : (
            <div className="mt-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 6))}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[--color-border] p-8 text-[color:var(--color-muted-foreground)] transition-colors hover:border-[--color-accent]/40"
              >
                <ImagePlus className="size-8" />
                <span className="text-[length:var(--text-body-sm)]">
                  {files.length > 0 ? `${files.length} foto(s) selecionada(s)` : "Toque para anexar fotos (até 6 páginas)"}
                </span>
              </button>
            </div>
          )}

          <div className="mt-6 sm:flex sm:justify-end">
            <Button
              size="lg"
              variant="gradient"
              onClick={handleSubmit}
              loading={submitting}
              className="w-full sm:w-auto"
            >
              {submitting ? "Corrigindo…" : "Corrigir redação"}
              {!submitting && <Sparkles className="size-4" />}
            </Button>
          </div>
        </Card>
      </Container>
    </div>
  );
}

function RedacaoResult({ result, onReset }: { result: GradeEssayFuvestResult; onReset: () => void }) {
  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-25 blur-2xl animate-blob" />

      <Container size="default" className="relative py-12 md:py-16">
        <Badge
          className="gap-1.5"
          style={{ backgroundColor: "var(--color-accent)", color: "white", border: "none" }}
        >
          <CheckCircle2 className="size-3" />
          Correção concluída
        </Badge>
        <h1 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight">
          Nota: <span className="text-gradient-accent">{result.scores.total}/100</span>
        </h1>

        {/* Critérios */}
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {CRITERIA.map((c) => {
            const score = result.scores[c.key];
            const pct = (score / c.max) * 100;
            return (
              <div key={c.key} className="rounded-xl border border-[--color-border] bg-[color:var(--color-card)] p-4">
                <div className="flex items-center justify-between">
                  <span className="text-[length:var(--text-body-sm)] font-medium">{c.label}</span>
                  <span data-tabular="true" className="font-display font-semibold" style={{ color: "var(--color-accent-deep)" }}>
                    {score}/{c.max}
                  </span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[color:var(--color-muted)]">
                  <div
                    className="h-full"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: pct >= 72 ? "var(--color-success)" : pct >= 48 ? "var(--color-warning)" : "var(--color-danger)",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Penalizações */}
        {result.detected_issues.length > 0 && (
          <div className="mt-6 rounded-xl border-2 border-[color:var(--color-danger)]/30 bg-[color:var(--color-danger)]/5 p-5">
            <p className="flex items-center gap-2 text-[length:var(--text-body-sm)] font-semibold" style={{ color: "var(--color-danger)" }}>
              <TriangleAlert className="size-4" />
              Problemas detectados
            </p>
            <ul className="mt-2 flex flex-wrap gap-2">
              {result.detected_issues.map((i) => (
                <li key={i}>
                  <Badge variant="danger">{ISSUE_LABELS[i] ?? i}</Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {result.transcription && (
          <details className="mt-6 rounded-xl border border-[--color-border] bg-[color:var(--color-muted)]/30 p-4">
            <summary className="cursor-pointer text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
              Transcrição da redação manuscrita
            </summary>
            <p className="mt-3 whitespace-pre-line text-[length:var(--text-body-sm)] leading-relaxed">
              {result.transcription}
            </p>
          </details>
        )}

        <section className="mt-8">
          <h2 className="font-display text-[length:var(--text-body-lg)] font-semibold">Feedback</h2>
          <p className="mt-3 whitespace-pre-line text-[length:var(--text-body)] leading-relaxed text-[color:var(--color-muted-foreground)]">
            {result.feedback}
          </p>
        </section>

        <section className="mt-8 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-6">
          <h2 className="font-display text-[length:var(--text-body-lg)] font-semibold">Plano de reescrita</h2>
          <p className="mt-3 whitespace-pre-line text-[length:var(--text-body)] leading-relaxed text-[color:var(--color-muted-foreground)]">
            {result.rewrite_plan}
          </p>
        </section>

        <div className="mt-8 flex gap-3">
          <Button variant="primary" onClick={onReset}>
            <PencilLine className="size-4" />
            Corrigir outra
          </Button>
          <Button asChild variant="outline">
            <Link to="/usp/painel">Voltar ao painel</Link>
          </Button>
        </div>
      </Container>
    </div>
  );
}
